import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiForgotPassword } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react';

/**
 * /forgot-password — public page reached from the "Forgot password?" link
 * on the Login page.
 *
 * Backend deliberately returns `{ok: true}` even for unknown emails so
 * this endpoint cannot be used to enumerate accounts. We match that on
 * the UI: same success screen regardless of whether the email exists.
 */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await apiForgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      // 422 (invalid email format) is the only realistic error path here —
      // any other failure the server soft-swallows and returns ok: true.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send reset email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 text-center">
          <img
            src="https://static.wixstatic.com/media/47e68b_9ba20c13e75f4752b5585a413624d1cc~mv2.png/v1/fill/w_342,h_72,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Icon%20%2B%20Lutrom%20%2B%20Engineering.png"
            alt="Lutrom Engineering"
            className="h-9 w-auto inline-block"
            style={{ filter: 'invert(1) brightness(2)' }}
          />
        </div>
        <div className="p-6">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-password-sent">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 mb-3">
                <MailCheck className="h-6 w-6" />
              </div>
              <h1 className="text-xl font-display tracking-tight text-slate-900 mb-2">Check your inbox</h1>
              <p className="text-sm text-slate-600 mb-6">
                If an account exists for <span className="font-medium text-slate-900">{email}</span>,
                we&rsquo;ve emailed you a link to reset your password. The link is valid for 1 hour.
              </p>
              <p className="text-xs text-slate-500 mb-4">
                Didn&rsquo;t get anything? Check your spam folder, or double-check the email address you used.
              </p>
              <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900 underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Password reset</div>
                <h1 className="text-2xl font-display tracking-tight text-slate-900 mt-1">
                  Forgot your password?
                </h1>
                <p className="text-sm text-slate-600 mt-2">
                  Enter the email you use to sign in and we&rsquo;ll send you a link to choose a new one.
                </p>
              </div>
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  className="mt-1.5"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoFocus
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-slate-900 hover:bg-slate-800 h-11"
                data-testid="forgot-password-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
              </Button>
              <div className="pt-2 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
